'use client'

import { ReservationButton } from '@/app/book/components/shared/ReservationButton'
import { NotificationBadge } from '@/components/notifications/NotificationBadge'
import { navigationItems } from '@/config/nav/main-navigation'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useFeatureFlags } from '@/lib/features/feature-flags-context'
import { NotificationProvider } from '@/lib/services/notifications/use-notifications'
import { ChoiceLogo } from '@/media/choice-graphics-logo'
import { ChoiceMark } from '@/media/choice-graphics-mark'
import {
  ButtonProps,
  cn
} from '@heroui/react'
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
} from '@/components/ui/navbar'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { CartDrawerV2 } from '../../cart/cart-drawer-v2'
import { Icon } from '@/components/ui/icon'
import { MobileMenuDrawer } from '../mobile-menu/mobile-menu-drawer'
import { toggleMobileMenu, useMobileMenuOpen } from '../mobile-menu/mobile-menu-store'
import { HeaderCart } from './header-cart'
import { QuoteButton } from '../quote-button'
import UserMenu from './user-menu/user-menu'

// The header bar is dark chrome (see Navbar below), matching every
// design-system template. Icon buttons there are transparent with a
// chrome-fg hairline border and glyph, per the templates' `rgba(230,234,239,.22)`
// border / on-dark icon colour on the header's action row, not the
// paper-scope --default/--border pair used elsewhere in the app.
export const headerButtonProps: ButtonProps = {
  isIconOnly: true,
  className: cn(
    'hover:scale-110 transition-all duration-300',
    'text-[color:var(--cw-chrome-fg)]',
    'bg-transparent border border-[rgba(230,234,239,.22)]',
    'rounded-full w-10 h-10 min-w-10 p-0 flex items-center justify-center'
  )
} as const

/** Routes where Book Now should be prominent (full button with text).
 *  Rule: marketing pages get the prominent pill; transactional/funnel pages
 *  (cart, checkout, pay, book, receipt, appointment) stay subdued so they
 *  don't compete with the page's own flow. */
const PUBLIC_ROUTES = [
  '/',
  '/services',
  '/products',
  '/gallery',
  '/articles',
  '/contact',
  '/get-a-quote',
  '/quote',
  '/warranty-claim',
  '/members',
  '/fleet-quote',
  '/projects',
  '/franchise-application',
]
/** Staff-only sections: drop the customer marketing nav, Book Now, and cart from the top bar.
 *  Slim AppHeader still renders on these routes to keep the brand + notifications + user menu
 *  available across all staff pages. Per-section navigation is rendered by the route's own
 *  layout (AdminNav inside /admin, IconRail+BottomTabBar inside /dashboard, etc.). */
const STAFF_ROUTES = ['/dashboard', '/admin', '/installer']

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.some(route =>
    route === '/' ? pathname === '/' : pathname.startsWith(route)
  )
}

function isStaffRoute(pathname: string): boolean {
  return STAFF_ROUTES.some(route => pathname.startsWith(route))
}

interface AppHeaderProps {
  hideCart?: boolean
  ecommerceEnabled?: boolean
}

export function AppHeader({ hideCart, ecommerceEnabled = false }: AppHeaderProps = {}) {
  const pathname = usePathname()
  const user = useCurrentUser()
  const userId = user?.id
  const menuOpen = useMobileMenuOpen()
  const { bookingEnabled } = useFeatureFlags()
  const staffRoute = isStaffRoute(pathname)
  const bookNowVariant = isPublicRoute(pathname) ? 'prominent' : 'subdued'
  const shouldHideCart = hideCart || staffRoute

  // Filter "Home" from desktop nav: logo serves that purpose. Staff routes
  // delegate navigation to their own layouts; top-bar nav is empty there.
  const desktopNavItems = staffRoute
    ? []
    : navigationItems.filter(item => item.href !== '/' && (ecommerceEnabled || item.href !== '/products'))

  const headerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)
  const scrollOffset = useRef(0)

  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height
      setHeaderHeight(h)
      document.documentElement.style.setProperty('--app-header-h', `${h}px`)
      // Live visible height = full height minus how far the header has slid up.
      // Overlays (drawers) pin their top to this so they stay flush with the
      // header's bottom edge even while it scroll-hides. --app-header-h itself
      // must stay static: the layout spacer and content offset depend on it.
      document.documentElement.style.setProperty(
        '--app-header-visible-h',
        `${Math.max(0, h - scrollOffset.current)}px`
      )
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Track scroll to slide the header within its container.
  // scrollOffset accumulates direction changes, clamped to [0, H].
  // 0 = fully visible (bottom half), H = fully hidden (top half).
  useEffect(() => {
    const H = headerHeight || 64
    let lastY = window.scrollY
    const onScroll = () => {
      const y = window.scrollY
      const delta = y - lastY
      lastY = y
      scrollOffset.current = Math.max(0, Math.min(H, scrollOffset.current + delta))
      headerRef.current?.style.setProperty('transform', `translateY(-${scrollOffset.current}px)`)
      // Keep overlays glued to the header's moving bottom edge (see ResizeObserver).
      document.documentElement.style.setProperty(
        '--app-header-visible-h',
        `${H - scrollOffset.current}px`
      )
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [headerHeight])

  // The booking wizard (/book) owns its chrome end-to-end via BookingHeader, so
  // the site header (and its layout spacer) must not render there: otherwise it
  // stacks a second header on top of the wizard's. The checkout wizard (/checkout)
  // owns its chrome the same way via WizardHeader. Early-return AFTER all hooks
  // to respect the Rules of Hooks.
  if (pathname === '/book' || pathname.startsWith('/book/')) return null
  if (pathname === '/checkout' || pathname.startsWith('/checkout/')) return null

  return (
    <NotificationProvider userId={userId}>
      {/* Layout spacer for the fixed header: colocated here so it disappears
          together with the header on routes where AppHeader returns null. */}
      <div className='h-[var(--app-header-h,64px)]' aria-hidden />
      {/* Scroll-hide header: fixed container is 2× header height. Top half
          sits above the viewport (clipped). Bottom half is visible. The
          header slides between the halves driven by scroll delta, 1:1. */}
      <div
        className='fixed z-[1100] w-full pointer-events-none overflow-hidden left-0'
        style={{ top: `-${headerHeight || 64}px`, height: `${(headerHeight || 64) * 2}px` }}
      >
        <div style={{ height: `${headerHeight || 64}px` }} aria-hidden />
        {/* Dark bar, per every design-system template (Homepage/Booking/Quote/
            Colours all agree): sticky header background is --cw-chrome with
            chrome-fg type, not the paper/ink light-scope treatment. Individual
            elements below carry their own chrome-fg colour since the site's
            [data-theme] scope stays light here (only this one bar deviates). */}
        <div ref={headerRef} className='w-full pointer-events-auto'>
        <Navbar
          className={cn(
            'max-w-full py-3 backdrop-blur-sm',
            'bg-[color:var(--cw-chrome)] border-b border-[rgba(230,234,239,.16)]'
          )}
          // Full-bleed bar, content constrained to the same 1200px/px-6
          // grid as V2Section so the logo and actions align with page content.
          classNames={{ wrapper: 'mx-auto max-w-[1200px] px-6' }}
        >
          {/* Logo */}
          <NavbarContent justify='start' className='w-auto pr-2 max-w-min sm:mr-4'>
            <NavbarBrand className='w-auto max-w-[130px] sm:max-w-[180px] md:max-w-[240px]'>
              <Link
                href='/'
                className='flex items-center gap-2'
                aria-label='Choice Graphics home page'
                // Matches the design-system brand-logo card's dark lockup:
                // sun mark + chrome-fg wordmark on the dark header bar,
                // overriding the site-wide ink logo colour just for this
                // lockup via the same --cw-logo-* indirection ChoiceLogo
                // already reads.
                style={{ '--cw-logo-primary': 'var(--cw-chrome-fg)', '--cw-logo-secondary': 'var(--cw-chrome-fg)' } as React.CSSProperties}
              >
                <ChoiceMark className='h-6 md:h-7' />
                {/* At 360px the md wordmark made the lockup 186.6px wide and
                    its box overlapped the 40px cart control by 2.6px. Step
                    the wordmark down below sm only; the mark, the control
                    sizes and the 24px bar padding all stay on the v3 header
                    spec (26px mark, 40px circular controls). */}
                <ChoiceLogo
                  size='md'
                  orientation='inline'
                  className='text-[16px]/[1.02] sm:text-[19.6px]/[1.02]'
                />
              </Link>
            </NavbarBrand>
          </NavbarContent>

          {/* Desktop nav links. lg and up only: at md (768 to 1023) eight
              links plus Book Now and the icon row overflowed the 1200px
              column and clipped Contact, Book Now, and the user menu off
              the right edge. Tablets get the hamburger below instead. */}
          <NavbarContent justify='start' className='hidden gap-1 lg:flex xl:gap-2'>
            {desktopNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  'flex items-center text-sm px-1.5 xl:px-2 transition-opacity whitespace-nowrap',
                  'text-[color:var(--cw-chrome-fg)]',
                  pathname === item.href
                    ? 'font-semibold opacity-100'
                    : 'font-medium opacity-70 hover:opacity-100'
                )}
              >
                {item.label}
              </Link>
            ))}
          </NavbarContent>

          {/* Actions */}
          <NavbarContent justify='end' className='flex flex-row items-center gap-1 sm:gap-2'>
            {/* Book Now: hidden on staff routes and on mobile (bottom nav handles it).
                This is the one hot-pink button on the site (see ReservationButton's
                'prominent' variant). The design-system templates show a single header
                CTA, not a second "Get a Quote" button alongside it. */}
            {!staffRoute && (
              <div className='hidden md:flex items-center'>
                {/* Booking off (BOOKING_ENABLED unset on this host): the same
                    slot carries Get a Quote so the header keeps one CTA. */}
                {!bookingEnabled ? (
                  <QuoteButton
                    variant={bookNowVariant}
                    className={bookNowVariant === 'subdued' && typeof headerButtonProps.className === 'string' ? headerButtonProps.className : undefined}
                  />
                ) : bookNowVariant === 'prominent' ? (
                  <ReservationButton variant='prominent' />
                ) : (
                  <ReservationButton variant='subdued' {...(({ variant: _v, ...rest }) => rest)(headerButtonProps)} />
                )}
              </div>
            )}

            {/* Cart button: all viewports (the bottom nav no longer carries cart); hidden on staff routes. */}
            {!shouldHideCart && (
              <div className='flex items-center'>
                <HeaderCart {...headerButtonProps} />
              </div>
            )}

            {/* Notifications: signed-in users only. A logged-out visitor has
                nothing to be notified about, and on a 360px phone the third
                icon crowded the wordmark. */}
            {user && <NotificationBadge {...headerButtonProps} />}

            {/* User menu */}
            <UserMenu {...headerButtonProps} />

            {/* Site menu trigger for tablets (md to lg): no inline links and
                no bottom bar at these widths, so this is the only way into the
                sections. Phones open the same drawer from the bottom bar's
                Menu tab, so the button is hidden below md. Staff routes keep
                their own navigation. */}
            {!staffRoute && (
              <button
                type='button'
                onClick={toggleMobileMenu}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls='site-menu'
                className={cn(headerButtonProps.className, 'hidden md:flex lg:hidden')}
              >
                <Icon icon={menuOpen ? 'cw-close' : 'menu'} className='h-5 w-5' />
              </button>
            )}
          </NavbarContent>
        </Navbar>
        </div>
      </div>

      {/* Cart drawer rendered outside the fixed header container so it's
          visible on ALL viewports (mobile included). The button that opens
          it lives in the header on all viewports. */}
      {!shouldHideCart && <CartDrawerV2 />}

      {/* Small-screen site menu, mounted once here (outside the fixed header
          container) so both its triggers share one drawer. */}
      {!staffRoute && <MobileMenuDrawer ecommerceEnabled={ecommerceEnabled} />}
    </NotificationProvider>
  )
}
